import {
  normalizeTalentLoadout,
  validateTalentLoadout,
  type TalentLoadout,
} from './talents';

export type RewardMode = 'gold-only' | 'talent-only' | 'conditional' | 'talent-and-gold';

export const MAX_LEVEL = 30;
export const MAX_TALENT_POINTS = 30;
export const TALENT_RESET_COST = 200;

export interface UserProgress {
  userId: string;
  talentPoints: number;
  gold: number;
  level: number;
  talentLoadout: TalentLoadout;
}

export interface WorkoutRewardConfig {
  rewardMode: RewardMode;
  talentReward: number;
  goldReward: number;
}

export interface EarnedWorkoutRewards {
  level: number;
  talent: number;
  gold: number;
}

export function createInitialUserProgress(userId: string): UserProgress {
  return {
    userId,
    talentPoints: 0,
    gold: 0,
    level: 0,
    talentLoadout: {},
  };
}

export function normalizeUserProgress(userId: string, progress: Partial<UserProgress>): UserProgress {
  const talentPoints = normalizeTalentPoints(progress.talentPoints);
  const talentLoadout = normalizeTalentLoadout(progress.talentLoadout);
  const talentValidation = validateTalentLoadout(talentLoadout, talentPoints);

  return {
    userId,
    talentPoints,
    gold: normalizeNonNegativeNumber(progress.gold),
    level: normalizeLevel(progress.level),
    talentLoadout: talentValidation.valid ? talentValidation.talentLoadout : {},
  };
}

export function resolveWorkoutRewards(
  progress: Pick<UserProgress, 'level' | 'talentPoints'>,
  rewardConfig: WorkoutRewardConfig,
): EarnedWorkoutRewards {
  const level = normalizeLevel(progress.level);
  const talentPoints = normalizeTalentPoints(progress.talentPoints);
  const talentCapacity = Math.max(0, MAX_TALENT_POINTS - talentPoints);
  const includesTalentReward = rewardConfig.rewardMode !== 'gold-only' && rewardConfig.talentReward > 0;
  const includesGoldReward = rewardConfig.rewardMode === 'gold-only'
    || rewardConfig.rewardMode === 'talent-and-gold'
    || (rewardConfig.rewardMode === 'conditional' && level >= MAX_LEVEL);

  return {
    level: level < MAX_LEVEL ? 1 : 0,
    talent: includesTalentReward ? roundTalentPoints(Math.min(rewardConfig.talentReward, talentCapacity)) : 0,
    gold: includesGoldReward
      ? normalizeNonNegativeNumber(rewardConfig.goldReward)
      : 0,
  };
}

export function normalizeLevel(value: unknown): number {
  const level = Number(value ?? 0);
  if (!Number.isFinite(level)) return 0;
  return Math.min(MAX_LEVEL, Math.max(0, Math.floor(level)));
}

export function normalizeTalentPoints(value: unknown): number {
  const talentPoints = Number(value ?? 0);
  if (!Number.isFinite(talentPoints)) return 0;
  return roundTalentPoints(Math.min(MAX_TALENT_POINTS, Math.max(0, talentPoints)));
}

export function normalizeNonNegativeNumber(value: unknown): number {
  const number = Number(value ?? 0);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, number);
}

export function roundTalentPoints(value: number): number {
  return Math.round(value * 1000000) / 1000000;
}

export { validateTalentLoadout } from './talents';
export type { TalentLoadout } from './talents';
